import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Button, message, Typography, Row, Col, Spin } from 'antd';
import { DownloadOutlined, CopyOutlined, ShareAltOutlined } from '@ant-design/icons';
import { DashboardContainer } from 'components/Container/Container';
import Api from 'api';

const { Title, Text, Paragraph } = Typography;

const PageHeader = styled.div`
  margin-bottom: 32px;

  h1 {
    margin-bottom: 8px;
  }

  .subtitle {
    color: #8c8c8c;
    font-size: 16px;
  }
`;

const TemplateCard = styled(Card)<{ $selected?: boolean }>`
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid ${props => props.$selected ? '#209d7e' : '#d9d9d9'};
  height: 100%;

  &:hover {
    border-color: #209d7e;
    box-shadow: 0 4px 12px rgba(32, 157, 126, 0.15);
  }

  .ant-card-body {
    padding: 24px;
    text-align: center;
  }

  .template-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .template-name {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .template-description {
    color: #8c8c8c;
    font-size: 14px;
  }
`;

const PreviewCard = styled(Card)`
  margin-bottom: 24px;

  .ant-card-body {
    padding: 32px;
    background: #f5f5f5;
    text-align: center;
  }

  .preview-placeholder {
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #8c8c8c;
  }

  .preview-image {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ActionButton = styled(Button)`
  &.generate-button {
    background-color: #209d7e !important;
    border-color: #209d7e !important;

    &:hover:not(:disabled) {
      background-color: #1a8168 !important;
      border-color: #1a8168 !important;
    }
  }
`;

const CaptionCard = styled(Card)`
  margin-top: 24px;

  .caption-text {
    font-family: 'Courier New', monospace;
    background: #f5f5f5;
    padding: 16px;
    border-radius: 4px;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const templates = [
  {
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Clean and simple design with your key stats',
    icon: '✨',
  },
  {
    id: 'nature',
    name: 'Nature',
    description: 'Beautiful nature-themed background with stats',
    icon: '🌿',
  },
  {
    id: 'achievement',
    name: 'Achievement',
    description: 'Celebrate your accomplishments with badges',
    icon: '🏆',
  },
];

interface Props {}

const StatCardShare: React.FC<Props> = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('minimalist');
  const [generating, setGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState<string>('');
  const [loadingCaption, setLoadingCaption] = useState(false);

  const handleGenerate = async () => {
    try {
      setGenerating(true);

      // Generate stat card
      const { data } = await Api.post('/dashboard/generate-stat-card', {
        template: selectedTemplate,
      });

      if (data.data.image_url) {
        setGeneratedImageUrl(data.data.image_url);
        message.success('Stat card generated successfully!');

        // Fetch caption
        fetchCaption();
      }

      setGenerating(false);
    } catch (error: any) {
      console.error('Error generating stat card:', error);
      const errorMsg = error?.response?.data?.messages?.[0] || 'Failed to generate stat card';
      message.error(errorMsg);
      setGenerating(false);
    }
  };

  const fetchCaption = async () => {
    try {
      setLoadingCaption(true);
      const { data } = await Api.get('/dashboard/generate-caption');

      if (data.data.caption) {
        setCaption(data.data.caption);
      }

      setLoadingCaption(false);
    } catch (error: any) {
      console.error('Error fetching caption:', error);
      setLoadingCaption(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImageUrl) return;

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = generatedImageUrl;
    link.download = `species-list-pro-stats-${selectedTemplate}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('Image downloaded!');
  };

  const handleCopyCaption = () => {
    if (!caption) return;

    navigator.clipboard.writeText(caption);
    message.success('Caption copied to clipboard!');
  };

  return (
    <DashboardContainer>
      <PageHeader>
        <Title level={1}>Share Your Progress</Title>
        <div className="subtitle">
          Generate shareable stat cards for social media
        </div>
      </PageHeader>

      <Card title="Choose a Template" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          {templates.map((template) => (
            <Col xs={24} sm={12} md={8} key={template.id}>
              <TemplateCard
                $selected={selectedTemplate === template.id}
                onClick={() => setSelectedTemplate(template.id)}
              >
                <div className="template-icon">{template.icon}</div>
                <div className="template-name">{template.name}</div>
                <div className="template-description">{template.description}</div>
              </TemplateCard>
            </Col>
          ))}
        </Row>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <ActionButton
            type="primary"
            size="large"
            icon={<ShareAltOutlined />}
            onClick={handleGenerate}
            loading={generating}
            className="generate-button"
          >
            {generating ? 'Generating...' : 'Generate Stat Card'}
          </ActionButton>
        </div>
      </Card>

      <PreviewCard title="Preview">
        {generatedImageUrl ? (
          <div>
            <img
              src={generatedImageUrl}
              alt="Generated stat card"
              className="preview-image"
            />
            <div style={{ marginTop: 24 }}>
              <ActionButton
                type="default"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                style={{ marginRight: 8 }}
              >
                Download Image
              </ActionButton>
            </div>
          </div>
        ) : (
          <div className="preview-placeholder">
            {generating ? (
              <>
                <Spin size="large" />
                <Text style={{ marginTop: 16 }}>Generating your stat card...</Text>
              </>
            ) : (
              <>
                <ShareAltOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                <Text style={{ marginTop: 16 }}>
                  Select a template and click "Generate Stat Card" to preview
                </Text>
              </>
            )}
          </div>
        )}
      </PreviewCard>

      {(caption || loadingCaption) && (
        <CaptionCard
          title="Social Media Caption"
          extra={
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyCaption}
              disabled={!caption}
            >
              Copy
            </Button>
          }
        >
          {loadingCaption ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Spin />
              <Text style={{ marginLeft: 12 }}>Generating caption...</Text>
            </div>
          ) : (
            <div className="caption-text">{caption}</div>
          )}
        </CaptionCard>
      )}

      <Card style={{ marginTop: 24, background: '#f0f9f6' }}>
        <Title level={4}>Sharing Tips</Title>
        <Paragraph>
          <ul>
            <li>Download your stat card and share it on Instagram, TikTok, or Twitter</li>
            <li>Use the generated caption to make your post more engaging</li>
            <li>Tag @specieslistpro to get featured on our community showcase</li>
            <li>Generate new cards as you make progress to show your growth</li>
          </ul>
        </Paragraph>
      </Card>
    </DashboardContainer>
  );
};

export default StatCardShare;
